import usePopUpAtOnce from "components/hooks/usePopUpAtOnce";
import { useAlert } from "components/UI/alertSakeBar/alertProvider";
import sysConf from "config";
import { filter, find, get } from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useCurrentClass } from "store/classes/selectors";
import {
  setPopUpHandler,
  updatePopUpHandler,
} from "store/sentenceStarter/actions";
import {
  useSentenceStarter,
  useSentenceStarterPopUp,
} from "store/sentenceStarter/selectors";
import {
  handleGetSentenceStarter
} from "store/sentenceStarter/thunks";
import { useUserProfileConfig } from "store/users/selectors";
import { getAwsAuthToken } from "utils/lib/authHelper";
import { cleanHTMLString } from "utils/methods/textMethod";
import AILoader from "./loader/aiLoader";
import ConfirmationMessage from "./types/confirmationMessage";
import ListLesson from "./types/listLesson";
import SentenceStarterOption from "./types/sentenceStarterOption";
import CreateSentenceStarterForm from "./types/createSentenceStarterForm";
import ViewerSentenceStarter from "./types/viewerSentenceStarter";
import { useCurrentOrg } from "store/organizations/selectors";
import SentryWrap from 'components/Sentry';

const SentenceStarterPopUp = ({ currentUser, bookId, pageId, book }) => {
  const showAlert = useAlert();
  const currentClass = useCurrentClass();
  const popConfig = useSentenceStarterPopUp();
  const dispatch = useDispatch();
  const sentenceStarterRes = useSentenceStarter();
  const [aiLoader, setAiLoader] = useState(false);
  const [streamEnd, setStreamEnd] = useState(false);
  const [streamValue, setStreamValue] = useState({
    is_straming:false,
    value:''
  }) //stream value
  const { isAiPermissionEnabled } = useUserProfileConfig();
  const { updatePopUpAtOnce } = usePopUpAtOnce();
  const currentPageIndex = useMemo(() => {
    return book?.pages?.findIndex(p => p.pageId == pageId);
  }, [book?.pages, pageId]);

  const sentenceStarter = useMemo(() => {
    if (sentenceStarterRes && sentenceStarterRes?.length > 0) {
      const filtered = sentenceStarterRes?.filter((x) => {
        const hasPageMatch = Number(x.pageId) === Number(pageId);
        const hasContent = x?.starter_content && 
                          typeof x.starter_content === 'string' && 
                          x.starter_content.trim().length > 0;
        return hasPageMatch && hasContent;
      }) || [];
      return filtered;
    } else {
      return [];
    }
  }, [pageId, sentenceStarterRes]);

  const filterLesson = useMemo(() => {
    const data =
      find(
        sentenceStarter,
        (data) =>
          data?.userId === currentUser?.id &&
          data?.access_by?.includes(currentUser?.id) &&
          data?.starter_content &&
          typeof data.starter_content === 'string' &&
          data.starter_content.trim().length > 0
      ) || {};
    return data;
  }, [currentUser, sentenceStarter]);

  const filterShareLesson = useMemo(() => {
    return (
      filter(
        sentenceStarter,
        (data) =>
          data?.userId !== currentUser?.id &&
          data?.access_by?.includes(currentUser?.id) &&
          data?.starter_content &&
          typeof data.starter_content === 'string' &&
          data.starter_content.trim().length > 0
      ) || []
    );
  }, [sentenceStarter, currentUser]);

  const bookData = useMemo(() => {
    return {
      superLesson: book?.superLesson,
      differnation: book?.differnationLesson,
      sentenceData: book?.sentence_starter_data,
    };
  }, [book, pageId]);

  const hasSenteceStarter = useMemo(() => {
    const pages = book?.pages || [];
    const isPageInRange = (item) => {
      if (!item.from || !item.to || !pages.length) {
        // Fallback to page number comparison if from/to not available
        return Number(pageId) === Number(item.page);
      }
      
      // Convert from and to pageIds to indices
      const fromIndex = pages.findIndex(x => x.pageId == item.from);
      const toIndex = pages.findIndex(x => x.pageId == item.to);
      
      //console.log(fromIndex, toIndex, currentPageIndex,123)
      // Check if currentPageIndex is within the range (inclusive)
      return currentPageIndex >= fromIndex && currentPageIndex <= toIndex && fromIndex >= 0 && toIndex >= 0;
    };

    const superLesson = bookData?.superLesson
      ? (() => {
          const foundItem = bookData.superLesson.find((p) => p.page == pageId);
          return foundItem || null;
        })()
      : null;

    const differnationLesson = bookData?.differentiationLesson
      ? (() => {
          const foundItem = bookData.differnation.find((p) =>
            p.page == pageId
          );
          return foundItem || null;
        })()
      : null;

    const sentenceData = bookData?.sentenceData
      ? (() => {
          const foundItem = bookData.sentenceData.find((p) => p.page == pageId);
          return foundItem || null;
        })()
      : null;

    if (sentenceData) {
      return {
        from: sentenceData?.from,
        to: sentenceData?.to,
      };
    } else if (differnationLesson) {
      return {
        from: differnationLesson?.from || superLesson?.from,
        to: differnationLesson?.to || superLesson?.to,
      };
    } else if (superLesson) {
      return {
        from: superLesson?.from,
        to: superLesson?.to,
      };
    }

    return {
      from: "",
      to: "",
    };
  }, [bookData, pageId, currentPageIndex, book?.pages]);

  const handleUpdateOpenPopUp = async (data, action = "update") => {
    try {
      if (action === "update") {
        await dispatch(updatePopUpHandler(data));
      } else if (action === "set") {
        await dispatch(setPopUpHandler(data));
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    handleUpdateOpenPopUp(
      { open: false, type: "", confirmationDialog: false },
      "set"
    );
  }, [pageId]);

  useEffect(() => {
    if (
      popConfig?.type === "" ||
      popConfig?.type === "viewSharedSentenceStarter" ||
      !popConfig ||
      popConfig === undefined
    ) {
      return;
    }
    handleUpdateOpenPopUp(
      { open: false, type: "", confirmationDialog: false },
      "set"
    );
  }, [pageId]);

  // const getPageFromSentenceStarter = useCallback(() => {
  //   const mask_from = getNumberMask(hasSenteceStarter?.from);
  //   const mask_to = getNumberMask(hasSenteceStarter?.to);

  //   return mask_from && mask_to ? `(P. ${mask_from}-${mask_to})` : "";
  // }, [hasSenteceStarter]);

  const hasErrorInContent = (content) => {
    if (!content || typeof content !== 'string') return false;
    const cleanedContent = content.toLowerCase();
    
    const hasSorryError = cleanedContent.includes("i'm sorry") && 
                         (cleanedContent.includes("can't assist") || cleanedContent.includes("cannot assist"));
    
    const hasUnableToAnalyze = cleanedContent.includes("unable to analyze") || 
                               cleanedContent.includes("i'm unable to analyze");
    
    const hasGeneralTopicError = cleanedContent.includes("can help generate sentence starters based on a general topic") ||
                                  cleanedContent.includes("please provide a topic");
    
    return hasSorryError || hasUnableToAnalyze || hasGeneralTopicError;
  };

  async function streamData(inputData, onChunk) {
    try {
      const data = {
        bookId: bookId,
        pageId: pageId,
        userId: currentUser.id,
      };
      const token = await getAwsAuthToken();
      const prodUrl = `${sysConf.apiGateway.AI_BASE_NODE_URL}/ai/sentence_starter/generate`;
      const localUrl = `http://localhost:4000/ai/sentence_starter/generate`;
      const response = await fetch(prodUrl, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          "x-auth-source": sysConf.isDev ? "test" : "prod",
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(inputData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to start generation' }));
        throw new Error(errorData.message || errorData.body || 'Failed to start stream');
      }
      
      if (!response.body) {
        throw new Error('Stream failed to start');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';
      let fullTextAccumulator = '';
      let hasReceivedContent = false;  
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const text = decoder.decode(value, { stream: true });
        const lines = text.split('\n').filter(Boolean);

        for (const line of lines) {
          if (line.startsWith('data:') && line.replace('data: ', '').trim() === '[DONE]') {
            // onFetchLesson();
            // setAlertSakeBar({ open: true, message: 'Created Successfully', severity: 'success' });
            // setCreateLesson(false);
            setStreamEnd(false);
            setStreamValue(prev => ({
              ...prev,
              is_straming: false
            }));
            if (hasReceivedContent) {
              const cleanedFinalContent = cleanHTMLString(fullTextAccumulator);
              const hasValidContent = cleanedFinalContent && 
                                     cleanedFinalContent.trim().length > 0 &&
                                     !hasErrorInContent(cleanedFinalContent);
              
              if (hasValidContent) {
                showAlert("Sentence starter created successfully!", "success");
                await dispatch(handleGetSentenceStarter(data));
              } else {
                throw new Error('No valid content was generated by the AI');
              }
            } else {
              throw new Error('No content was generated by the AI');
            }
            return;
          }
          
          if (line.startsWith('data:') && !line.includes('[DONE]')) {
            hasReceivedContent = true;
            const content = line.replace('data: ', '');
            fullTextAccumulator += content;
            
            const cleanedContent = cleanHTMLString(fullTextAccumulator);
            
            if (hasErrorInContent(cleanedContent)) {
              throw new Error('AI declined to generate content. Please try again or contact support.');
            }
            
            onChunk(cleanedContent);
          }
        }
      }
      
      if (!hasReceivedContent) {
        throw new Error('No content was generated by the AI');
      }
      
      const cleanedFinalContent = cleanHTMLString(fullTextAccumulator);
      const hasValidContent = cleanedFinalContent && 
                             cleanedFinalContent.trim().length > 0 &&
                             !hasErrorInContent(cleanedFinalContent);
      if (!hasValidContent) {
        throw new Error('No valid content was generated by the AI');
      }
      
    } catch (error) {
      console.error('Stream error:', error);
      // setAlertSakeBar({
      //   open: true,
      //   message: 'Failed to create lesson. Please try again.',
      //   severity: 'error',
      // });
      // setCreateLesson(false);
      setStreamEnd(false);
      setAiLoader(false);

      let errorMessage = `Failed to generate sentence starter: ${error.message}`;

      if (error.message.includes('Server error: 429')) {
        errorMessage =
          "You've reached the daily limit for creating sentence starters. Please try again tomorrow.";
      } else if (error.message.includes('Server error: 500')) {
        errorMessage = 'Server error occurred. Please try again in a few moments.';
      } else if (error.message.includes('No content was generated')) {
        errorMessage =
          'AI was unable to generate content. Please try again or contact support if the issue persists.';
      }

      showAlert(`${errorMessage} Please try again.`, 'error');
      updatePopUpAtOnce(false);
      throw error;
    }
  }

  const handleGenerateSenentceStarter = async (sentence_starter_data = {}, retryCount = 0) => {
    if (!isAiPermissionEnabled) {
      showAlert('Please enable AI permission to create a sentence starter.', 'error');
      return;
    }
    setAiLoader(true);
    setStreamEnd(false);
    setStreamValue({
      is_straming: false,
      value: ''
    });
    
    handleUpdateOpenPopUp({ open: false, type: "", confirmationDialog: false });
    const { limit, ...other } = filterLesson;
    try {
      const payload = {
        ...other,
        bookId,
        pageId,
        from_page: hasSenteceStarter.from,
        to_page: hasSenteceStarter.to,
        userId: currentUser.id,
        sentence_starter_data,
        access_by: [currentUser.id],
        class_id: currentClass?.id,
        mode: import.meta.env.VITE_APP_STAGE?.trim() === "prod" ? "prod" : "dev",

      };
      //stream value
      let is_started = false;
      let initval = ``
      let fullHTML = "";
  
      setStreamEnd(true);
      let hasErrorDetected = false;
      await streamData(payload, async (chunk) => {
        fullHTML += chunk;
        const hasError = hasErrorInContent(fullHTML);
        
        if (hasError) {
          hasErrorDetected = true;
          handleUpdateOpenPopUp({ open: false, type: "", confirmationDialog: false });
          updatePopUpAtOnce(false);
        } else if (!hasErrorDetected) {
          handleUpdateOpenPopUp({
            open: true,
            type: "savedSentenceStarter",
            confirmationDialog: false,
          });
        }
        
        setStreamValue(props => ({
          ...props,
          is_straming: true,
          value: chunk
        }))
      });
      
      const finalHasError = hasErrorInContent(fullHTML);
      
      if (finalHasError || hasErrorDetected) {
        handleUpdateOpenPopUp({ open: false, type: "", confirmationDialog: false });
        updatePopUpAtOnce(false);
        showAlert("AI declined to generate sentence starter. Please try again.", "error");
      }

      setStreamEnd(false);
      setStreamValue(prev => ({
        ...prev,
        is_straming: false
      }));
      setAiLoader(false);
    } catch (error) {
      console.error('Generation error:', error);
      setAiLoader(false);
      setStreamEnd(false);
      setStreamValue({
        is_straming: false,
        value: ''
      });
      
      if (error.message.includes('Failed to generate sentence starter')) {
        return;
      }
      
      showAlert(
        `Unable to generate sentence starter: ${error.message}. Please try again.`, 
        "error"
      );
      
      const errorMessage = error.message || '';
      const isOpenAIAssistError = errorMessage.includes("can't assist with that") || 
                                   errorMessage.includes("can't assist") || errorMessage.includes("I'm sorry");
      
      if (isOpenAIAssistError && retryCount === 0) {
        showAlert('AI declined to generate. Retrying automatically...', 'warning');
        setTimeout(() => {
          handleGenerateSenentceStarter(sentence_starter_data, 1);
        }, 2000);
      } else if (isOpenAIAssistError && retryCount >= 1) {
        showAlert('AI declined to generate sentence starter for these pages. Please try different pages or contact support.', 'error');
        updatePopUpAtOnce(false);
        // handleUpdateOpenPopUp({ open: false, type: "", confirmationDialog: false });
      } else {
        showAlert(error.message || 'Failed to generate sentence starter. Please try again with different pages.', 'error');
        updatePopUpAtOnce(false);
        // handleUpdateOpenPopUp({ open: false, type: "", confirmationDialog: false });
      }
    }
  };

  return (
    <SentryWrap>
      {popConfig?.open && popConfig?.type === "sentenceStarterOption" && (
        <SentenceStarterOption
          currentUser={currentUser}
          handleUpdateOpenPopUp={handleUpdateOpenPopUp}
          savedLesson={filterLesson}
          sharedLesson={filterShareLesson}
          handleGenerateSenentceStarter={handleGenerateSenentceStarter}
        />
      )}

      {popConfig?.open && popConfig?.type === "createSentenceStarterForm" && (
        <CreateSentenceStarterForm
          handleUpdateOpenPopUp={handleUpdateOpenPopUp}
          handleGenerateSenentceStarter={handleGenerateSenentceStarter}
          savedLesson={filterLesson}
          hasErrorInContent={hasErrorInContent(filterLesson?.starter_content)}
          isAiPermissionEnabled={isAiPermissionEnabled}
          showAlert={showAlert}
        />
      )}

      {popConfig?.confirmationDialog && (
        <ConfirmationMessage
          currentUser={currentUser}
          handleUpdateOpenPopUp={handleUpdateOpenPopUp}
          handleGenerateSenentceStarter={handleGenerateSenentceStarter}
          createFormData={popConfig?.createFormData}
        />
      )}

      {aiLoader && (
        <AILoader
          open={aiLoader}
          loader={false}
          from={"ebook"}
          currentUser={currentUser}
        />
      )}

      {popConfig?.open && popConfig?.type === "savedSentenceStarter" && (
        <ViewerSentenceStarter
          handleUpdateOpenPopUp={handleUpdateOpenPopUp}
          streamValue={streamValue} //stream value
          filterMyData={
            filterLesson && Object?.keys(filterLesson)?.length > 0
              ? filterLesson
              : popConfig?.data
          }
          currentUser={currentUser}
          streamEnd={streamEnd}
        />
      )}

      {popConfig?.open && popConfig?.type === "viewSharedSentenceStarter" && (
        <ViewerSentenceStarter
          streamValue={streamValue} //stream value
          handleUpdateOpenPopUp={handleUpdateOpenPopUp}
          filterMyData={popConfig?.data}
          currentUser={currentUser}
          streamEnd={streamEnd}
        />
      )}

      {popConfig?.open && popConfig?.type === "sharedSentenceStarter" && (
        <ListLesson
          filterData={filterShareLesson}
          currentUser={currentUser}
          handleUpdateOpenPopUp={handleUpdateOpenPopUp}
        />
      )}
    </SentryWrap>
  );
};

export default SentenceStarterPopUp;
